import { Button, Divider, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { PimDashboardState } from "context/PimDashboardContext";
import ImageSlider from "components/PimDashboard/CommonComponents/ImageSlider/ImageSlider";
import { AddRounded } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import {
	costingTableTypes,
	costingUrlKey,
	filterRequiredStyleColumns,
	openCostingFabricAssocObjKey,
	openCostingOtherChargesAssocObjkey,
	openCostingPackingTrimsAssocObjKey,
	openCostingPoCostAssocObjkey,
	openCostingSewingTrimsAssocObjKey,
	openCostingValueAddAssocObjKey,
	supplierColumn,
	totalQtyColumn,
} from "./data";
import { useEffect, useState } from "react";
import { constraintsOptions, getColDefaultValue, isColumnDefault, selectedOpenCostingPath } from "components/PimDashboard/data";
import AddFileButton from "components/PimDashboard/CommonComponents/AddFileButton/AddFileButton";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { inputTypes, statusOptions } from "data/globalData";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { custom } from "data/authData";
import DisplayDetailsWithEditTab from "./DisplayDetailsWithEditTab/DisplayDetailsWithEditTab";
import { defaultFabricTotalRow } from "../../../CreateOpenCosting/FabricTable/data";
import FabricTable from "../../../CreateOpenCosting/FabricTable/FabricTable";
import SewingTrimsTable from "../../../CreateOpenCosting/SewingTrimsTable/SewingTrimsTable";
import PackingTrimsTable from "../../../CreateOpenCosting/PackingTrimsTable/PackingTrimsTable";
import ValueAdditionTable from "../../../CreateOpenCosting/ValueAdditionTable/ValueAdditionTable";
import GarmentTotalTable from "../../../CreateOpenCosting/GarmentTotalTable/GarmentTotalTable";
import FinalTotalTable from "../../../CreateOpenCosting/FinalTotalTable/FinalTotalTable";
import PoCostTable from "../../../CreateOpenCosting/PoCostTable/PoCostTable";
import { otherChargesDefaultData } from "../../../CreateOpenCosting/FinalTotalTable/data";
import DynamicTrimConsumption from "../../../CreateOpenCosting/DynamicTrimConsumption/DynamicTrimConsumption";
import { applyDiscountingCol, openCostingTrimsDynamicConsumpAssocObjKey, paymentTermColumn } from "../../../CreateOpenCosting/data";

const imgWidth = 170;
const imgHeight = 210;

const CostingDetails = ({ selectedCosting, setSelectedCosting }) => {
	const { allAttributesObj: styleAllAttributesObj } = PimDashboardState();
	const { snackBar, setLoadingOpen, setSnackBarOpen, setSnackBar } = GlobalState();
	const { user } = AuthState();
	const navigate = useNavigate();
	let [searchParams, setSearchParams] = useSearchParams();

	const disableEdit = user?.role?.[custom.COSTING_VIEWER.key] === true;

	const [loading, setLoading] = useState({});

	const [optionDetailsColumns, setOptionDetailsColumns] = useState([]);

	const [fabricRows, setFabricRows] = useState([]);
	const [fabricTotalRow, setFabricTotalRow] = useState({ id: new Date().valueOf() });
	const [sewingTrimsRows, setSewingTrimsRows] = useState([]);
	const [sewingTrimsTotalRow, setSewingTrimsTotalRow] = useState({ id: new Date().valueOf() });
	const [packingTrimsRows, setPackingTrimsRows] = useState([]);
	const [packingTrimsTotalRow, setPackingTrimsTotalRow] = useState({ id: new Date().valueOf() });
	const [dynamicTrimsRows, setDynamicTrimsRows] = useState([]);
	const [dynamicTrimsTotalRow, setDynamicTrimsTotalRow] = useState({ id: new Date().valueOf() });
	const [valueAddRows, setValueAddRows] = useState([]);
	const [valueAddTotalRow, setValueAddTotalRow] = useState({ id: new Date().valueOf() });
	const [finalTotalRows, setFinalTotalRows] = useState(otherChargesDefaultData);
	const [poCostRows, setPoCostRows] = useState([]);
	const [finalTotal, setFinalTotal] = useState({ id: new Date().valueOf() });
	const [garmentTotal, setGarmentTotal] = useState({});
	const [editMode, setEditMode] = useState("");

	useEffect(() => {
		if (styleAllAttributesObj && !optionDetailsColumns?.length) {
			const columns = filterRequiredStyleColumns(styleAllAttributesObj)?.map((attr) => {
				if (
					attr?.key !== "brand" &&
					(attr?.input_type === inputTypes.SELECT.key || attr?.input_type === inputTypes.MULTIPLE_SELECT.key)
				)
					return { ...attr, input_type: inputTypes.ENTER.key };
				return attr;
			});
			setOptionDetailsColumns(columns);
		}
	}, []);

	useEffect(() => {
		const fetch = async () => {
			setLoadingOpen(true);
			setLoading(true);
			setSelectedCosting();
			try {
				const res = await axios.get(`/api/sourcing/costing_full_data/${id}`);
				const result = res.data.result;
				setSelectedCosting(() => {
					let obj = { ...result };
					delete obj[openCostingFabricAssocObjKey];
					delete obj[openCostingSewingTrimsAssocObjKey];
					delete obj[openCostingPackingTrimsAssocObjKey];
					delete obj[openCostingTrimsDynamicConsumpAssocObjKey];
					delete obj[openCostingValueAddAssocObjKey];
					delete obj[openCostingOtherChargesAssocObjkey];
					delete obj[openCostingPoCostAssocObjkey];
					return obj;
				});
				setFabricRows(result[openCostingFabricAssocObjKey] ?? []);
				setSewingTrimsRows(result[openCostingSewingTrimsAssocObjKey] ?? []);
				setPackingTrimsRows(result[openCostingPackingTrimsAssocObjKey] ?? []);
				setDynamicTrimsRows(result[openCostingTrimsDynamicConsumpAssocObjKey] ?? []);
				setValueAddRows(result[openCostingValueAddAssocObjKey] ?? []);
				setFinalTotalRows(result[openCostingOtherChargesAssocObjkey] ?? []);
				setPoCostRows(result[openCostingPoCostAssocObjkey] ?? []);
				setLoadingOpen(false);
				setLoading(false);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
				setLoadingOpen(false);
				setLoading(false);
			}
		};
		let id = searchParams.get(costingUrlKey);
		if (id && id !== selectedCosting?.id) fetch();
		else {
			setSelectedCosting(null);
		}
	}, [searchParams]);

	const handleImageSelectAndUpdate = async (e) => {
		console.log(e.target.files, "smnfbadsfbndsfvdsbnvfjsd");
		const file = e.target.files[0];
		try {
			setLoadingOpen(true);

			const formData = new FormData();
			formData.append("images", file);

			await axios
				.post("/api/sourcing/upload_costing_images", formData, {
					headers: {
						"content-type": "multipart/form-data",
					},
				})
				.then(({ data }) => {
					let url = data.result[0]?.publicUrl;
					handleOptionDetailsSave({ url, style_ref_no: selectedCosting?.style_ref_no }, () => {});
				});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};

	const handleOptionDetailsSave = async (data, closeModal) => {
		setLoadingOpen(true);
		try {
			await axios.put(`/api/sourcing/update_costing_details/${data?.style_ref_no}`, data).then((res) => {
				setSelectedCosting((prev) => ({ ...prev, ...data }));
				setLoadingOpen(false);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: `Data Saved Successfully`,
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				closeModal();
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};
	const handleFabricTableSave = async (data, deletedRowIds) => {
		const res = await axios.put(`/api/sourcing/update_fabric_rows/${selectedCosting?.style_ref_no}`, {
			rows: data,
			finalTotalRows,
			poCostRows,
			deletedRowIds,
		});
		return res.data.result;
	};
	const handleSewingTrimsTableSave = async (data, deletedRowIds) => {
		const res = await axios.put(`/api/sourcing/update_sewing_trims_rows/${selectedCosting?.style_ref_no}`, {
			rows: data,
			finalTotalRows,
			poCostRows,
			deletedRowIds,
		});
		return res.data.result;
	};
	const handlePackingTrimsTableSave = async (data, deletedRowIds) => {
		const res = await axios.put(`/api/sourcing/update_packing_trims_rows/${selectedCosting?.style_ref_no}`, {
			rows: data,
			deletedRowIds,
			finalTotalRows,
			poCostRows,
		});
		return res.data.result;
	};
	const handleDynamicTrimsConsumpTableSave = async (data, deletedRowIds) => {
		const res = await axios.put(`/api/sourcing/update_trims_dynamic_consump_rows/${selectedCosting?.style_ref_no}`, {
			rows: data,
			deletedRowIds,
			finalTotalRows,
			poCostRows,
		});
		return res.data.result;
	};
	const handleValueAddTableSave = async (data, deletedRowIds) => {
		const res = await axios.put(`/api/sourcing/update_value_add_rows/${selectedCosting?.style_ref_no}`, {
			rows: data,
			deletedRowIds,
			finalTotalRows,
			poCostRows,
		});
		return res.data.result;
	};
	const handleFinalTotalTableSave = async (data, deletedRowIds) => {
		const res = await axios.put(`/api/sourcing/update_final_total_rows/${selectedCosting?.style_ref_no}`, {
			rows: data,
			deletedRowIds,
			poCostRows,
		});
		return res.data.result;
	};
	const handlePoCostDataSave = async (data, deletedRowIds) => {
		const res = await axios.put(`/api/sourcing/update_po_cost_rows/${selectedCosting?.style_ref_no}`, {
			rows: data,
			deletedRowIds,
			optionDetails: {
				[applyDiscountingCol.key]: selectedCosting?.[applyDiscountingCol.key],
				[paymentTermColumn.key]: selectedCosting?.[paymentTermColumn.key],
				last_updated_by: user.email,
			},
		});
		return res.data.result;
	};
	if (loading) return <></>;
	else
		return (
			<Stack spacing={2} pt={2} pb={10}>
				{/* <Divider /> */}
				<Box display="flex" justifyContent="space-between" width="100%" columnGap="2%">
					{/* left */}
					<Stack>
						<Box display="flex" width="100%" pb={1} justifyContent="space-between">
							{selectedCosting?.url && (
								<>
									<Typography variant="h6">Image</Typography>
									<Box>
										<AddFileButton name={"Add Image"} onChange={handleImageSelectAndUpdate} sx={{}}>
											<Button size="small" sx={{ textTransform: "capitalize" }} variant="outlined">
												Update Image
											</Button>
										</AddFileButton>
									</Box>
								</>
							)}
						</Box>
						<Box
							width={imgWidth}
							height={imgHeight}
							sx={{ border: `1px solid ${grey[200]}` }}
							display="flex"
							alignItems="center"
							justifyContent="center">
							{!selectedCosting?.url ? (
								<Box>
									<AddFileButton name={"Add Image"} onChange={handleImageSelectAndUpdate} sx={{}}>
										<Button size="large" startIcon={<AddRounded />}>
											Add Image
										</Button>
									</AddFileButton>
								</Box>
							) : (
								<Box width={imgWidth} height={imgHeight} display="flex" flexWrap="wrap" rowGap={2}>
									<ImageSlider
										imgWidth={imgWidth}
										imgHeight={imgHeight}
										column="url"
										images={[{ url: selectedCosting?.url }]}
									/>
								</Box>
							)}
						</Box>
					</Stack>
					{/* <Divider orientation="vertical" flexItem /> */}
					{/* right */}
					<Box sx={{ minWidth: "50%" }} flexGrow={1}>
						<Typography variant="h6">Option Details</Typography>

						<DisplayDetailsWithEditTab
							columns={optionDetailsColumns}
							data={selectedCosting}
							setData={setSelectedCosting}
							updateFunction={handleOptionDetailsSave}
							disableEdit={disableEdit}
						/>
						<DisplayDetailsWithEditTab
							columns={[totalQtyColumn, supplierColumn]}
							data={selectedCosting}
							setData={setSelectedCosting}
							updateFunction={handleOptionDetailsSave}
							disableEdit={true}
							name={""}
						/>
					</Box>
				</Box>
				<FabricTable
					rows={fabricRows}
					setRows={setFabricRows}
					totalRow={fabricTotalRow}
					setTotalRow={setFabricTotalRow}
					editMode={editMode === costingTableTypes.fabric.key}
					setEditMode={(mode) => setEditMode(mode ? costingTableTypes.fabric.key : "")}
					mode={editMode === costingTableTypes.fabric.key || editMode === "" ? "edit" : ""}
					handleSave={handleFabricTableSave}
				/>
				<SewingTrimsTable
					rows={sewingTrimsRows}
					setRows={setSewingTrimsRows}
					totalRow={sewingTrimsTotalRow}
					setTotalRow={setSewingTrimsTotalRow}
					editMode={editMode === costingTableTypes.sewing_trims.key}
					setEditMode={(mode) => setEditMode(mode ? costingTableTypes.sewing_trims.key : "")}
					mode={editMode === costingTableTypes.sewing_trims.key || editMode === "" ? "edit" : ""}
					handleSave={handleSewingTrimsTableSave}
				/>
				<PackingTrimsTable
					rows={packingTrimsRows}
					setRows={setPackingTrimsRows}
					totalRow={packingTrimsTotalRow}
					setTotalRow={setPackingTrimsTotalRow}
					editMode={editMode === costingTableTypes.packing_trims.key}
					setEditMode={(mode) => setEditMode(mode ? costingTableTypes.packing_trims.key : "")}
					mode={editMode === costingTableTypes.packing_trims.key || editMode === "" ? "edit" : ""}
					handleSave={handlePackingTrimsTableSave}
				/>
				<DynamicTrimConsumption
					rows={dynamicTrimsRows}
					setRows={setDynamicTrimsRows}
					totalRow={dynamicTrimsTotalRow}
					setTotalRow={setDynamicTrimsTotalRow}
					editMode={editMode === costingTableTypes.carton_tape.key}
					setEditMode={(mode) => setEditMode(mode ? costingTableTypes.carton_tape.key : "")}
					mode={editMode === costingTableTypes.carton_tape.key || editMode === "" ? "edit" : ""}
					handleSave={handleDynamicTrimsConsumpTableSave}
				/>
				<ValueAdditionTable
					rows={valueAddRows}
					setRows={setValueAddRows}
					totalRow={valueAddTotalRow}
					setTotalRow={setValueAddTotalRow}
					editMode={editMode === costingTableTypes.value_add.key}
					setEditMode={(mode) => setEditMode(mode ? costingTableTypes.value_add.key : "")}
					mode={editMode === costingTableTypes.value_add.key || editMode === "" ? "edit" : ""}
					handleSave={handleValueAddTableSave}
				/>

				<Box display="flex" width="100%" justifyContent="space-around">
					<Box width="40%">
						<GarmentTotalTable
							rows={[fabricTotalRow, sewingTrimsTotalRow, packingTrimsTotalRow, dynamicTrimsTotalRow, valueAddTotalRow]}
							setRows={() => {}}
							garmentTotal={garmentTotal}
							setGarmentTotal={setGarmentTotal}
						/>
					</Box>
					<Box width="55%">
						<FinalTotalTable
							finalTotal={finalTotal}
							setFinalTotal={setFinalTotal}
							rows={finalTotalRows}
							setRows={setFinalTotalRows}
							garmentTotalObj={garmentTotal}
							optionDetails={selectedCosting}
							editMode={editMode === costingTableTypes.final_total.key}
							setEditMode={(mode) => setEditMode(mode ? costingTableTypes.final_total.key : "")}
							mode={editMode === costingTableTypes.final_total.key || editMode === "" ? "edit" : ""}
							handleSave={handleFinalTotalTableSave}
						/>
					</Box>
				</Box>
				<PoCostTable
					rows={poCostRows}
					setRows={setPoCostRows}
					finalTotalObj={finalTotal}
					optionDetails={selectedCosting}
					setOptionDetails={setSelectedCosting}
					editMode={editMode === costingTableTypes.po_cost.key}
					setEditMode={(mode) => setEditMode(mode ? costingTableTypes.po_cost.key : "")}
					mode={editMode === costingTableTypes.po_cost.key || editMode === "" ? "edit" : ""}
					handleSave={handlePoCostDataSave}
				/>
			</Stack>
		);
};

export default CostingDetails;
